import { useCallback, useMemo } from 'react';
import type { TractNRI } from '../types';
import {
  BulkEditSlotsPanel,
  type BulkEditSavePayload,
  type BulkEditScopedSavePayload,
} from './bulk-edit-slots-panel';
import { useBulkEditAfeOptions } from './hooks';
import type { BulkEditFormState } from './types';

interface BulkEditSlotsPanelConnectedProps {
  open: boolean;
  onClose: () => void;
  selectedRows: TractNRI[];
  allRows?: TractNRI[];
  onSave?: (payload: BulkEditSavePayload) => void;
  resolveOriginal?: (
    row: TractNRI,
    key: keyof BulkEditFormState
  ) => string | undefined;
}

/**
 * Jotai-connected `BulkEditSlotsPanel` for the legacy eval-3 page. Loads the AFE
 * candidate options (intersection across the selected slots) via the Jotai
 * `useBulkEditAfeOptions` hook and feeds them into the presentational panel. Legacy is
 * always ENUMERATED, so this builds the `enumerated` scope from its flat props and
 * adapts the panel's scoped `onSave` payload back to the flat `BulkEditSavePayload` the
 * legacy callsite consumes — keeping that callsite's prop contract fully unchanged.
 *
 * @param {BulkEditSlotsPanelConnectedProps} props - The same props the panel took before the scope refactor.
 * @return {JSX.Element} The presentational panel wired to Jotai-loaded AFE options.
 */
export const BulkEditSlotsPanelConnected = ({
  open,
  onClose,
  selectedRows,
  allRows,
  onSave,
  resolveOriginal,
}: BulkEditSlotsPanelConnectedProps) => {
  // Same filter the panel used internally before the decouple, so the AFE
  // intersection is computed over exactly the selected slots.
  const selectedSlotIds = useMemo(
    () =>
      selectedRows
        .map((row) => row.slotId)
        .filter((slotId): slotId is string => Boolean(slotId)),
    [selectedRows]
  );

  const afeOptions = useBulkEditAfeOptions(selectedSlotIds);

  // Adapt the scoped payload back to the flat legacy shape (legacy never selects-all,
  // so it's always the enumerated variant).
  const handleSave = useCallback(
    (payload: BulkEditScopedSavePayload) => {
      if (payload.scope === 'enumerated') {
        onSave?.({
          slotIds: payload.slotIds,
          patch: payload.patch,
          resolvedAfe: payload.resolvedAfe,
        });
      }
    },
    [onSave]
  );

  return (
    <BulkEditSlotsPanel
      open={open}
      onClose={onClose}
      onSave={handleSave}
      scope={{
        kind: 'enumerated',
        selectedRows,
        allRows,
        afeOptions,
        resolveOriginal,
      }}
    />
  );
};
